import React, { useState } from 'react';
import client from '../api/client';
import toast from 'react-hot-toast';

function ActionCard({ title, description, buttonText, onAction, loading, accent = 'indigo', children }) {
  const colors = {
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
    rose: 'bg-rose-600 hover:bg-rose-700',
  };
  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
      <h3 className="text-white font-bold mb-1">{title}</h3>
      <p className="text-slate-400 text-sm mb-4">{description}</p>
      {children}
      <button
        onClick={onAction}
        disabled={loading}
        className={`mt-3 px-4 py-2 text-white text-sm font-medium rounded-lg transition ${colors[accent]} disabled:opacity-50 disabled:cursor-wait`}
      >
        {loading ? '⏳ İşleniyor...' : buttonText}
      </button>
    </div>
  );
}

export default function ContentManager() {
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingRegen, setLoadingRegen] = useState(false);
  const [loadingHoroscope, setLoadingHoroscope] = useState(false);
  const [loadingMoon, setLoadingMoon] = useState(false);
  const [loadingMoonSign, setLoadingMoonSign] = useState(false);
  const [loadingMoonDelete, setLoadingMoonDelete] = useState(false);
  const [loadingNotif, setLoadingNotif] = useState(false);
  const [horoscopeType, setHoroscopeType] = useState('weekly');
  const [moonYear, setMoonYear] = useState(new Date().getFullYear());
  const [moonMonth, setMoonMonth] = useState(new Date().getMonth() + 1);
  const [regenDate, setRegenDate] = useState(new Date().toISOString().slice(0, 10));
  const [results, setResults] = useState([]);

  const addResult = (title, data) => {
    setResults((prev) => [{ title, data, time: new Date().toLocaleTimeString('tr-TR') }, ...prev].slice(0, 10));
  };

  const generateDaily = async () => {
    setLoadingDaily(true);
    try {
      const res = await client.post('/api/v1/admin/generate-daily?lang=all', null, {
        headers: { 'x-admin-key': process.env.REACT_APP_ADMIN_API_KEY },
      });
      toast.success('Günlük mood üretildi!');
      addResult('Günlük Mood', res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoadingDaily(false);
    }
  };

  const regenerateDaily = async () => {
    setLoadingRegen(true);
    try {
      const res = await client.post(`/api/v1/admin/regenerate-daily-moods?target_date=${regenDate}`, null, {
        headers: { 'x-admin-key': process.env.REACT_APP_ADMIN_API_KEY },
      });
      toast.success('Mood yeniden üretildi!');
      addResult('Mood Yeniden Üretim', res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoadingRegen(false);
    }
  };

  const generateHoroscope = async () => {
    setLoadingHoroscope(true);
    try {
      const res = await client.post(`/api/v1/admin/generate-horoscopes?period_type=${horoscopeType}`, null, {
        headers: { 'x-admin-key': process.env.REACT_APP_ADMIN_API_KEY },
      });
      toast.success(`${horoscopeType} horoskop üretildi!`);
      addResult('Horoskop', res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoadingHoroscope(false);
    }
  };

  const generateMoon = async () => {
    setLoadingMoon(true);
    try {
      const res = await client.post(`/api/v1/admin/generate-month?year=${moonYear}&month=${moonMonth}`, null, {
        headers: { 'x-admin-key': process.env.REACT_APP_ADMIN_API_KEY },
      });
      toast.success('Ay takvimi üretildi!');
      addResult('Ay Takvimi', res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoadingMoon(false);
    }
  };

  const generateMoonSign = async () => {
    setLoadingMoonSign(true);
    try {
      const res = await client.post(`/api/v1/admin/generate-moon-sign-cache?year=${moonYear}&month=${moonMonth}&force=false`, null, {
        headers: { 'x-admin-key': process.env.REACT_APP_ADMIN_API_KEY },
      });
      toast.success('Burç bazlı ay takvimi üretildi!');
      addResult('Burç Ay Takvimi', res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoadingMoonSign(false);
    }
  };

  const sendTestNotification = async (period) => {
    setLoadingNotif(true);
    try {
      const res = await client.post(`/api/v1/admin/send-test-notification?period=${period}`, null, {
        headers: { 'x-admin-key': process.env.REACT_APP_ADMIN_API_KEY },
      });
      toast.success(`${period} bildirimi gönderildi!`);
      addResult('Bildirim', res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoadingNotif(false);
    }
  };

  const deleteMoonCache = async () => {
    if (!window.confirm(`${moonYear}-${String(moonMonth).padStart(2,'0')} ay cache'ini silmek istediğinize emin misiniz?`)) return;
    setLoadingMoonDelete(true);
    try {
      const res = await client.delete(`/api/v1/admin/moon-cache/${moonYear}/${moonMonth}`, {
        headers: { 'x-admin-key': process.env.REACT_APP_ADMIN_API_KEY },
      });
      toast.success('Ay cache temizlendi!');
      addResult('Cache Temizleme', res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoadingMoonDelete(false);
    }
  };

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">📅 İçerik Yönetimi</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Daily Mood */}
        <ActionCard
          title="🌤 Günlük Mood Üret"
          description="Tüm diller için bugünün genel mood içeriğini üretir (idempotent)."
          buttonText="Üret"
          onAction={generateDaily}
          loading={loadingDaily}
          accent="emerald"
        />

        {/* Regenerate Daily */}
        <ActionCard
          title="🔄 Günlük Mood Yeniden Üret"
          description="Belirli tarih için eski mood'ları silip baştan üretir."
          buttonText="Yeniden Üret"
          onAction={regenerateDaily}
          loading={loadingRegen}
          accent="rose"
        >
          <input
            type="date"
            value={regenDate}
            onChange={(e) => setRegenDate(e.target.value)}
            className="bg-slate-700 text-slate-300 text-sm rounded-lg px-3 py-1.5 border border-slate-600 outline-none"
          />
        </ActionCard>

        {/* Horoscope */}
        <ActionCard
          title="♈ Horoskop Üret"
          description="12 burç için haftalık veya aylık horoskop üretir."
          buttonText="Üret"
          onAction={generateHoroscope}
          loading={loadingHoroscope}
          accent="purple"
        >
          <select
            value={horoscopeType}
            onChange={(e) => setHoroscopeType(e.target.value)}
            className="bg-slate-700 text-slate-300 text-sm rounded-lg px-3 py-1.5 border border-slate-600 focus:border-purple-500 outline-none"
          >
            <option value="weekly">Haftalık</option>
            <option value="monthly">Aylık</option>
          </select>
        </ActionCard>

        {/* Moon Calendar */}
        <ActionCard
          title="🌙 Ay Takvimi Üret"
          description="Belirli ay için genel ay takvimi içeriğini üretir."
          buttonText="Üret"
          onAction={generateMoon}
          loading={loadingMoon}
          accent="indigo"
        >
          <div className="flex gap-2">
            <input
              type="number"
              value={moonYear}
              onChange={(e) => setMoonYear(parseInt(e.target.value))}
              className="w-24 bg-slate-700 text-slate-300 text-sm rounded-lg px-3 py-1.5 border border-slate-600 outline-none"
              placeholder="Yıl"
            />
            <input
              type="number"
              value={moonMonth}
              onChange={(e) => setMoonMonth(parseInt(e.target.value))}
              min="1"
              max="12"
              className="w-20 bg-slate-700 text-slate-300 text-sm rounded-lg px-3 py-1.5 border border-slate-600 outline-none"
              placeholder="Ay"
            />
          </div>
        </ActionCard>

        {/* Moon Sign Cache */}
        <ActionCard
          title="♊ Burç Bazlı Ay Takvimi"
          description="12 burç için ay takvimi yorumlarını üretir."
          buttonText="Üret"
          onAction={generateMoonSign}
          loading={loadingMoonSign}
          accent="amber"
        >
          <p className="text-xs text-slate-500">Yukarıdaki yıl/ay ayarlarını kullanır.</p>
        </ActionCard>

        {/* Delete Moon Cache */}
        <ActionCard
          title="🗑 Ay Cache Temizle"
          description="Belirli ayın burç bazlı cache verilerini siler."
          buttonText="Temizle"
          onAction={deleteMoonCache}
          loading={loadingMoonDelete}
          accent="rose"
        >
          <p className="text-xs text-slate-500">Yukarıdaki yıl/ay ayarlarını kullanır.</p>
        </ActionCard>

        {/* Test Notification */}
        <ActionCard
          title="🔔 Test Bildirimi Gönder"
          description="Uygun kullanıcılara test push bildirimi gönderir."
          buttonText="Sabah Gönder"
          onAction={() => sendTestNotification('morning')}
          loading={loadingNotif}
          accent="rose"
        >
          <button
            onClick={() => sendTestNotification('evening')}
            disabled={loadingNotif}
            className="text-xs text-slate-400 hover:text-slate-200 underline"
          >
            veya Akşam Gönder
          </button>
        </ActionCard>
      </div>

      {/* Results Log */}
      {results.length > 0 && (
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <h3 className="text-lg font-bold text-white mb-4">📋 Son Sonuçlar</h3>
          <div className="space-y-3">
            {results.map((r, i) => (
              <div key={i} className="bg-slate-700/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-white">{r.title}</span>
                  <span className="text-xs text-slate-500">{r.time}</span>
                </div>
                <pre className="text-xs text-slate-400 overflow-x-auto">{JSON.stringify(r.data, null, 2)}</pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
